import jsPDF from "jspdf";
import { PLAN_DATA } from "./demoConfig";
import { CASE_STUDIES } from "./DemoScreens";

const TEAL = [29, 158, 117];
const PURPLE = [124, 58, 237];
const DARK = [26, 26, 26];
const GRAY = [107, 107, 107];
const LIGHT_BG = [248, 249, 251];

function addBranding(doc) {
  // Top bar
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, 210, 8, "F");

  // Logo text
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...TEAL);
  doc.text("BulkJobApply", 20, 25);

  // Tagline
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text("Your Job Search, Handled.", 20, 32);

  // Top-right date
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  const date = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  doc.text(date, 190, 25, { align: "right" });

  // Divider
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.3);
  doc.line(20, 38, 190, 38);
}

function addFooter(doc, pageNum, totalPages) {
  const y = 282;
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.3);
  doc.line(20, y - 4, 190, y - 4);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("BulkJobApply | bulkjobapply.com | support@bulkjobapply.com", 20, y);
  doc.text(`Page ${pageNum} of ${totalPages}`, 190, y, { align: "right" });
}

function sectionTitle(doc, y, title) {
  doc.setFillColor(...TEAL);
  doc.roundedRect(20, y - 4, 3, 14, 1, 1, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...DARK);
  doc.text(title, 28, y + 6);
  return y + 18;
}

function checkItem(doc, y, text) {
  doc.setFillColor(...TEAL);
  doc.circle(26, y + 1, 2.5, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text("\u2713", 24.5, y + 2.8);
  doc.setTextColor(...DARK);
  doc.text(text, 33, y + 3);
  return y + 10;
}

export function generateDemoSummaryPDF(lead, settings, auth, selectedCaseStudies = []) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const planKey = lead.selectedPlan || lead.recommendedPlan;
  const plan = planKey ? PLAN_DATA[planKey] : null;
  const closerName = settings?.closerSignature || auth?.name || "Your advisor";
  const paymentLink = settings?.razorpayLink || settings?.paypalLink || "[payment link]";
  const painTags = (selectedCaseStudies || []).map(id => {
    const cs = CASE_STUDIES.find(c => c.id === id);
    return cs ? cs.tag : null;
  }).filter(Boolean);

  // ═══ PAGE 1 ═══
  addBranding(doc);

  // Title
  let y = 50;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...DARK);
  doc.text(`Demo Summary for ${lead.clientName}`, 20, y);

  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text(`Prepared by ${closerName} on ${new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`, 20, y);

  // Lead Info Box
  y += 14;
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(20, y, 170, 32, 4, 4, "F");
  doc.setDrawColor(230, 230, 230);
  doc.roundedRect(20, y, 170, 32, 4, 4, "S");

  const infoY = y + 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text("Name:", 28, infoY);
  doc.text("Target Role:", 28, infoY + 10);
  doc.text("Target Market:", 28, infoY + 20);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GRAY);
  doc.text(`${lead.clientName} ${lead.clientLastName || ""}`.trim(), 55, infoY);
  doc.text(lead.targetRole || "—", 60, infoY + 10);
  doc.text(lead.targetMarket || "—", 65, infoY + 20);

  if (lead.targetSalary) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text("Expected Salary:", 120, infoY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(`$${parseFloat(lead.targetSalary).toLocaleString()}/mo`, 155, infoY);
  }

  if (lead.monthsSearching) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text("Months Searching:", 120, infoY + 10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(`${lead.monthsSearching}`, 160, infoY + 10);
  }

  // Pain points
  y += 42;
  if (painTags.length > 0) {
    y = sectionTitle(doc, y, "Challenges Discussed");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    painTags.forEach(tag => {
      doc.text(`•  ${tag}`, 30, y + 2);
      y += 8;
    });
    y += 4;
  }

  // What we'll do
  y = sectionTitle(doc, y, "What BulkJobApply Will Do For You");

  y = checkItem(doc, y, "Up to 780 human-applied career page appl. per month");
  y = checkItem(doc, y, "Each resume tailored to match the specific job description");
  y = checkItem(doc, y, "4,000+ Easy Apply appl. via ChillApply automation");
  y = checkItem(doc, y, "20 LinkedIn connection requests + 40 referral messages per day via ChillRefer");
  y = checkItem(doc, y, "Dedicated WhatsApp group with your account manager and assistant");
  y = checkItem(doc, y, "Real-time tracking dashboard — see every application live");
  y = checkItem(doc, y, "ATS-optimized resume rebuild by our HR specialist");
  y = checkItem(doc, y, "Applications start within 3 days of enrollment");

  // Recommended Plan
  y += 6;
  if (plan) {
    y = sectionTitle(doc, y, "Your Recommended Plan");

    // Plan box
    doc.setFillColor(...TEAL);
    doc.roundedRect(20, y, 170, 44, 4, 4, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text(plan.name, 30, y + 14);

    doc.setFontSize(24);
    doc.text(plan.price, 170, y + 14, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255, 200);
    plan.features.forEach((f, i) => {
      doc.text(`✓  ${f}`, 30, y + 24 + i * 7);
    });

    y += 54;
  }

  // FOMO + Payment
  y += 2;
  doc.setFillColor(255, 245, 235);
  doc.setDrawColor(216, 90, 48);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, y, 170, 22, 4, 4, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(216, 90, 48);
  doc.text("This offer expires in 24 hours", 105, y + 9, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text("Secure your spot now — we only take limited clients per market to ensure quality.", 105, y + 17, { align: "center" });

  y += 30;

  // Payment link
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text("Ready to start?", 20, y);

  y += 8;
  doc.setFillColor(...TEAL);
  doc.roundedRect(20, y, 170, 14, 4, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text("Complete Payment", 105, y + 9, { align: "center" });
  doc.link(20, y, 170, 14, { url: paymentLink });

  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(`Payment link: ${paymentLink}`, 20, y);

  // Founder signature
  y += 16;
  doc.setDrawColor(230, 230, 230);
  doc.line(20, y, 190, y);
  y += 10;

  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text(`"We've helped 375+ job seekers land interviews. Let us do the same for you."`, 20, y);

  y += 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text("Shubham Maheshwari", 20, y);

  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text("Founder, BulkJobApply", 20, y);

  addFooter(doc, 1, 1);

  // Save
  const fileName = `BulkJobApply_Summary_${lead.clientName.replace(/\s/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
  return fileName;
}
